import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Header from '../Common/Header';
import { api } from '../../services/api';
import config from '../../config/environment';
import './CandidateDetails.css';

// Party Color Mapping
const PARTY_COLORS = {
  'BJP': { primary: '#FF9933', secondary: '#138808' },
  'INC': { primary: '#19AAED', secondary: '#0D6EAF' },
  'JDU': { primary: '#006400', secondary: '#228B22' },
  'JD(U)': { primary: '#006400', secondary: '#228B22' },
  'RJD': { primary: '#008000', secondary: '#006400' },
  'AIMIM': { primary: '#00A100', secondary: '#008000' },
  'AAP': { primary: '#0066B3', secondary: '#004C8C' },
  'BSP': { primary: '#2B57AB', secondary: '#1F3F7A' },
  'CPI': { primary: '#FF0000', secondary: '#CC0000' },
  'CPI(M)': { primary: '#CC0000', secondary: '#990000' },
  'CPM': { primary: '#CC0000', secondary: '#990000' },
  'SP': { primary: '#FF2222', secondary: '#CC0000' },
  'NOTA': { primary: '#666666', secondary: '#444444' },
  'IND': { primary: '#808080', secondary: '#666666' },
  'DEFAULT': { primary: '#667eea', secondary: '#764ba2' }
};

const CandidateDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // ==================== CANDIDATE STATE ====================
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [symbolError, setSymbolError] = useState(false);

  // ==================== SHARE STATE ====================
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  
  // ==================== MODAL STATE ====================
  const [showAffidavitModal, setShowAffidavitModal] = useState(false);
  const [submittingForm, setSubmittingForm] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // ==================== FORM STATE - SIMPLIFIED ====================
  const [affidavitFormData, setAffidavitFormData] = useState({
    fullName: '',
    email: '',
    mobile: ''
  });
  
  // ==================== FORM ERRORS ====================
  const [formErrors, setFormErrors] = useState({});

  // ==================== USEEFFECTS ====================
  useEffect(() => {
    loadCandidate();
  }, [id]);

  // ==================== LOAD CANDIDATE ====================
  const loadCandidate = async () => {
    try {
      setLoading(true);
      const data = await api.getCandidateById(id);
      console.log('📋 Candidate data loaded:', data);
      setCandidate(data);
    } catch (error) {
      console.error('❌ Error loading candidate:', error);
      toast.error(t('common.error') || 'Error loading candidate details');
    } finally {
      setLoading(false);
    }
  };

  // ==================== HELPER FUNCTIONS ====================
  const getPartyColor = (partyShortName) => {
    return PARTY_COLORS[partyShortName] || PARTY_COLORS['DEFAULT'];
  };

  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:')) {
      return url;
    }
    const baseUrl = config.API_URL.replace(/\/api$/, '');
    return `${baseUrl}${url}`;
  };

  const maskMobile = (mobile) => {
    if (!mobile) return t('common.noData') || 'N/A';
    const str = String(mobile);
    if (str.length < 4) return str;
    return 'x'.repeat(str.length - 3) + str.slice(-3);
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const names = name.trim().split(' ');
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    }
    return names[0][0].toUpperCase();
  };

  const getSymbolUrl = () => {
    if (!candidate) return null;
    return candidate.customSymbolUrl || candidate.symbolUrl || candidate.partySymbolUrl || null;
  };

  const getAffidavitUrl = () => {
    if (!candidate?.affidavitPdfUrl) return null;
    const url = candidate.affidavitPdfUrl;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    const baseUrl = config.API_URL.replace(/\/api$/, '');
    return `${baseUrl}${url}`;
  };

  // ==================== FORM VALIDATION ====================
  const validateForm = () => {
    const errors = {};
    
    if (!affidavitFormData.fullName.trim()) {
      errors.fullName = t('voterCertificate.fillAllFields') || 'Full name is required';
    }
    
    // ✅ EMAIL IS OPTIONAL - ONLY VALIDATE IF PROVIDED
    if (affidavitFormData.email.trim() && !/\S+@\S+\.\S+/.test(affidavitFormData.email)) {
      errors.email = t('candidateDetails.invalidEmail') || 'Invalid email format';
    }
    
    if (!affidavitFormData.mobile.trim()) {
      errors.mobile = t('candidateDetails.mobileRequired') || 'Mobile number is required';
    } else if (!/^\d{10}$/.test(affidavitFormData.mobile.replace(/\D/g, ''))) {
      errors.mobile = t('candidateDetails.invalidMobile') || 'Mobile must be 10 digits';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ==================== MODAL HANDLERS ====================
  const handleOpenAffidavitModal = () => {
    setShowAffidavitModal(true);
    setFormErrors({});
  };

  const handleCloseAffidavitModal = () => {
    setShowAffidavitModal(false);
    setAffidavitFormData({
      fullName: '',
      email: '',
      mobile: ''
    });
    setFormErrors({});
    setSuccessMessage('');
  };

  // ==================== FORM CHANGE HANDLERS ====================
  const handleFormInputChange = (e) => {
    const { name, value } = e.target;
    setAffidavitFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // ==================== FORM SUBMIT ====================
  const handleAffidavitFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      console.log('❌ Form validation failed');
      return;
    }
    
    try {
      setSubmittingForm(true);
      setSuccessMessage('');
      
      console.log('📤 Submitting affidavit form...');
      
      // ✅ SUBMIT TO BACKEND
      const saveResponse = await api.submitAffidavitRequest({
        fullName: affidavitFormData.fullName.trim(),
        email: affidavitFormData.email?.trim() || null,  // ✅ Optional
        mobile: affidavitFormData.mobile.trim()
      });
      
      console.log('✅ Backend response:', saveResponse);
      
      if (!saveResponse.success) {
        throw new Error(saveResponse.error || 'Failed to save details');
      }
      
      // ✅ GET AFFIDAVIT URL
      const affidavitUrl = getAffidavitUrl();
      
      if (!affidavitUrl) {
        console.error('❌ No affidavit URL');
        toast.error('Affidavit PDF not available');
        handleCloseAffidavitModal();
        return;
      }
      
      // ✅ OPEN PDF IN NEW TAB
      console.log('📖 Opening PDF...');
      window.open(affidavitUrl, '_blank', 'noopener,noreferrer');
      
      // ✅ SHOW SUCCESS
      setSuccessMessage('✅ Details saved! PDF opening in new tab...');
      toast.success('Details saved successfully!');
      
      setTimeout(() => {
        handleCloseAffidavitModal();
      }, 2000);
      
    } catch (error) {
      console.error('❌ Error:', error.message);
      toast.error(error.message || 'Something went wrong');
    } finally {
      setSubmittingForm(false);
    }
  };

  // ==================== SHARE HANDLERS ====================
  const getShareUrl = () => {
    return `${window.location.origin}/candidate/${id}`;
  };

  const getShareText = () => {
    return `Check out ${candidate?.name} - ${candidate?.partyShortName} candidate from ${candidate?.constituencyName}`;
  };

  const handleShareTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(getShareText())}&url=${encodeURIComponent(getShareUrl())}`;
    window.open(url, '_blank');
    setShowShareMenu(false);
  };

  const handleShareFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getShareUrl())}`;
    window.open(url, '_blank');
    setShowShareMenu(false);
  };

  const handleShareWhatsApp = () => {
    const text = `${getShareText()} ${getShareUrl()}`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
    setShowShareMenu(false);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(getShareUrl());
    setShareCopied(true);
    setTimeout(() => setShareCopied(false), 2000);
    setShowShareMenu(false);
  };

  // ==================== RENDER ====================
  if (loading) {
    return (
      <div className="candidate-details-page">
        <Header />
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>{t('common.loading') || 'Loading...'}</p>
        </div>
      </div>
    );
  }

  if (!candidate) {
    return (
      <div className="candidate-details-page">
        <Header />
        <div className="empty-state">
          <i className="fas fa-user-times" aria-hidden="true"></i>
          <h2>{t('candidateDetails.notFound') || 'Candidate not found'}</h2>
          <button 
            onClick={() => navigate('/nominations')} 
            className="back-btn"
          >
            <i className="fas fa-arrow-left" aria-hidden="true"></i> 
            {t('common.backToNominations') || 'Back to Nominations'}
          </button>
        </div>
      </div>
    );
  }

  const partyColor = getPartyColor(candidate.partyShortName);
  const affidavitUrl = getAffidavitUrl();

  return (
    <div className="candidate-details-page">
      <Header />

      <div className="candidate-details-container">
        {/* Top Actions */}
        <div className="top-actions">
          <button 
            onClick={() => navigate(-1)} 
            className="back-button"
          >
            <i className="fas fa-arrow-left" aria-hidden="true"></i> 
            {t('common.back') || 'Back'}
          </button>
          
          <div className="share-container">
            <button 
              onClick={() => setShowShareMenu(!showShareMenu)} 
              className="share-button"
              style={{ background: partyColor.primary }}
            >
              <i className="fas fa-share-alt" aria-hidden="true"></i> 
              {t('candidateDetails.shareProfile') || 'Share Profile'}
            </button>
            
            {showShareMenu && (
              <div className="share-menu" role="menu">
                <button 
                  onClick={handleShareTwitter} 
                  className="share-option twitter"
                  role="menuitem"
                >
                  <i className="fab fa-twitter" aria-hidden="true"></i> Twitter
                </button>
                <button 
                  onClick={handleShareFacebook} 
                  className="share-option facebook"
                  role="menuitem"
                >
                  <i className="fab fa-facebook-f" aria-hidden="true"></i> Facebook
                </button>
                <button 
                  onClick={handleShareWhatsApp} 
                  className="share-option whatsapp"
                  role="menuitem"
                >
                  <i className="fab fa-whatsapp" aria-hidden="true"></i> WhatsApp
                </button>
                <button 
                  onClick={handleCopyLink} 
                  className="share-option copy"
                  role="menuitem"
                >
                  <i className="fas fa-link" aria-hidden="true"></i> 
                  {shareCopied ? t('candidateDetails.copied') : t('candidateDetails.copyLink')}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Header Section */}
        <div className="details-header" style={{
          background: `linear-gradient(135deg, ${partyColor.primary}, ${partyColor.secondary})`
        }}>
          <div className="header-content">
            <div className="candidate-photo-large">
              {candidate.photoUrl && !imageError ? (
                <img 
                  src={getFullImageUrl(candidate.photoUrl)} 
                  alt={candidate.name}
                  onError={() => setImageError(true)}
                />
              ) : (
                <span className="initials-large">{getInitials(candidate.name)}</span>
              )}
            </div>
            <div className="header-info">
              <h1>{candidate.name}</h1>
              <div className="party-section">
                <div className="party-badge-large">{candidate.partyShortName}</div>
                <span className="party-name-large">{candidate.partyName}</span>
                {getSymbolUrl() && !symbolError && (
                  <div className="party-symbol-large">
                    <img 
                      src={getFullImageUrl(getSymbolUrl())} 
                      alt={`${candidate.partyShortName} symbol`}
                      onError={() => setSymbolError(true)}
                    />
                  </div>
                )}
              </div>
              <p className="constituency-info">
                <i className="fas fa-map-marker-alt" aria-hidden="true"></i> 
                {candidate.constituencyName}
              </p>
            </div>
          </div>
        </div>

        {/* Details Grid */}
        <div className="details-grid">
          <div className="details-card">
            <h3><i className="fas fa-user" aria-hidden="true"></i> {t('candidateDetails.personalInfo') || 'Personal Information'}</h3>
            <div className="info-row">
              <span className="info-label">{t('results.age') || 'Age'}</span>
              <span className="info-value">{candidate.age || t('common.noData') || 'N/A'}</span>
            </div>
            <div className="info-row">
              <span className="info-label">{t('results.education') || 'Education'}</span>
              <span className="info-value">{candidate.education || t('common.noData') || 'N/A'}</span>
            </div>
            <div className="info-row">
              <span className="info-label">{t('results.profession') || 'Profession'}</span>
              <span className="info-value">{candidate.profession || t('common.noData') || 'N/A'}</span>
            </div>
          </div>

          <div className="details-card">
            <h3><i className="fas fa-address-book" aria-hidden="true"></i> {t('candidateDetails.contactInfo') || 'Contact Information'}</h3>
            {candidate.email && (
              <div className="info-row">
                <span className="info-label"><i className="fas fa-envelope" aria-hidden="true"></i> {t('candidateDetails.email') || 'Email'}</span>
                <span className="info-value"><a href={`mailto:${candidate.email}`} style={{ color: partyColor.primary }}>{candidate.email}</a></span>
              </div>
            )}
            {candidate.mobile && (
              <div className="info-row">
                <span className="info-label"><i className="fas fa-phone" aria-hidden="true"></i> {t('candidateDetails.mobile') || 'Mobile'}</span>
                <span className="info-value masked-mobile">{maskMobile(candidate.mobile)}</span>
              </div>
            )}
            {candidate.address && (
              <div className="info-row">
                <span className="info-label"><i className="fas fa-home" aria-hidden="true"></i> {t('candidateDetails.address') || 'Address'}</span>
                <span className="info-value">{candidate.address}</span>
              </div>
            )}
          </div>

          <div className="details-card">
            <h3><i className="fas fa-money-bill-wave" aria-hidden="true"></i> {t('candidateDetails.financialInfo') || 'Financial Information'}</h3>
            <div className="info-row">
              <span className="info-label">{t('candidateDetails.totalAssets') || 'Total Assets'}</span>
              <span className="info-value financial">₹ {candidate.assets ? candidate.assets.toLocaleString('en-IN') : t('common.noData') || 'N/A'}</span>
            </div>
            <div className="info-row">
              <span className="info-label">{t('candidateDetails.totalLiabilities') || 'Total Liabilities'}</span>
              <span className="info-value financial">₹ {candidate.liabilities ? candidate.liabilities.toLocaleString('en-IN') : t('common.noData') || 'N/A'}</span>
            </div>
            {candidate.assets && candidate.liabilities && (
              <div className="info-row">
                <span className="info-label">{t('candidateDetails.netWorth') || 'Net Worth'}</span>
                <span className="info-value financial-highlight">₹ {(candidate.assets - candidate.liabilities).toLocaleString('en-IN')}</span>
              </div>
            )}
          </div>

          <div className="details-card">
            <h3><i className="fas fa-gavel" aria-hidden="true"></i> {t('candidateDetails.criminalRecord') || 'Criminal Record'}</h3>
            <div className="info-row">
              <span className="info-label">{t('results.criminalCases') || 'Criminal Cases'}</span>
              <span className={`info-value criminal-badge ${candidate.criminalCases > 0 ? 'has-cases' : 'no-cases'}`}>
                {candidate.criminalCases || 0}
                {candidate.criminalCases === 0 && ` ✓ ${t('candidateDetails.cleanRecord') || 'Clean Record'}`}
              </span>
            </div>
          </div>
        </div>

        {/* Affidavit Section */}
        {affidavitUrl && (
          <div className="affidavit-section">
            <div className="affidavit-card">
              <div className="affidavit-header">
                <div className="affidavit-icon">
                  <i className="fas fa-file-pdf" aria-hidden="true"></i>
                </div>
                <div className="affidavit-info">
                  <h3>{t('candidateDetails.affidavitTitle') || 'Election Affidavit'}</h3>
                  <p>{t('candidateDetails.affidavitDesc') || 'Official candidate affidavit document'}</p>
                </div>
              </div>
              <div className="affidavit-actions">
                <button 
                  onClick={handleOpenAffidavitModal}
                  className="btn-view"
                  style={{ background: partyColor.primary }}
                >
                  <i className="fas fa-external-link-alt" aria-hidden="true"></i> 
                  {t('candidateDetails.openPDF') || 'View Affidavit'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Source Link */}
        {candidate.backgroundSourceUrl && (
          <div className="source-section">
            <a 
              href={candidate.backgroundSourceUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="source-link"
              style={{ borderColor: partyColor.primary, color: partyColor.primary }}
            >
              <i className="fas fa-external-link-alt" aria-hidden="true"></i>
              {t('results.viewFullProfile') || 'View Full Profile'}
            </a>
          </div>
        )}
      </div>

      {/* ✅ AFFIDAVIT MODAL - SIMPLIFIED */}
      {showAffidavitModal && (
        <div className="modal-overlay" onClick={handleCloseAffidavitModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><i className="fas fa-file-pdf" aria-hidden="true"></i> {t('candidateDetails.affidavitTitle') || 'View Affidavit'}</h2>
              <button 
                className="modal-close"
                onClick={handleCloseAffidavitModal}
                title={t('common.close') || 'Close'}
              >
                ×
              </button>
            </div>

            {successMessage && (
              <div className="success-banner">
                {successMessage}
              </div>
            )}

            <form onSubmit={handleAffidavitFormSubmit} className="affidavit-form">
              <div className="form-intro">
                <p>{t('candidateDetails.affidavitFormDesc') || 'Please provide your details to view the affidavit'}</p>
              </div>

              {/* Full Name */}
              <div className="form-group">
                <label htmlFor="fullName">
                  {t('candidateDetails.fullName') || 'Full Name'}
                  <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  placeholder={t('candidateDetails.enterFullName') || 'Enter your full name'}
                  value={affidavitFormData.fullName}
                  onChange={handleFormInputChange}
                  className={`form-input ${formErrors.fullName ? 'error' : ''}`}
                  required
                />
                {formErrors.fullName && <span className="error-message">{formErrors.fullName}</span>}
              </div>

              {/* Mobile */}
              <div className="form-group">
                <label htmlFor="mobile">
                  {t('candidateDetails.mobile') || 'Mobile Number'}
                  <span className="required">*</span>
                </label>
                <input
                  type="tel"
                  id="mobile"
                  name="mobile"
                  placeholder={t('candidateDetails.tenDigitMobile') || 'Enter 10-digit mobile number'}
                  value={affidavitFormData.mobile}
                  onChange={handleFormInputChange}
                  className={`form-input ${formErrors.mobile ? 'error' : ''}`}
                  maxLength="10"
                  required
                />
                {formErrors.mobile && <span className="error-message">{formErrors.mobile}</span>}
              </div>

              {/* Email - OPTIONAL */}
              <div className="form-group">
                <label htmlFor="email">
                  {t('candidateDetails.email') || 'Email'}
                  <span className="optional">(Optional)</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder={t('candidateDetails.enterEmail') || 'Enter your email (optional)'}
                  value={affidavitFormData.email}
                  onChange={handleFormInputChange}
                  className={`form-input ${formErrors.email ? 'error' : ''}`}
                />
                {formErrors.email && <span className="error-message">{formErrors.email}</span>}
              </div>

              

              {/* Form Actions */}
              <div className="form-actions">
                <button
                  type="button"
                  onClick={handleCloseAffidavitModal}
                  className="btn-cancel"
                >
                  {t('common.cancel') || 'Cancel'}
                </button>
                <button
                  type="submit"
                  className="btn-submit"
                  style={{ background: partyColor.primary }}
                  disabled={submittingForm}
                >
                  {submittingForm ? (
                    <>
                      <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
                      {' '} {t('common.loading') || 'Processing...'}
                    </>
                  ) : (
                    <>
                      <i className="fas fa-download" aria-hidden="true"></i>
                      {' '} {t('candidateDetails.viewAffidavit') || 'View Affidavit'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CandidateDetails;
